import getFlattenedFiles from '@picsart/cloud-storage/api/getFlattenedFiles';
import updateFileMeta from '@picsart/cloud-storage/api/updateFileMeta';
import uploadFileToSystemFolder from '@picsart/cloud-storage/api/uploadFileToSystemFolder';
import type {
  FileAttributeProps,
  IFile,
} from '@picsart/cloud-storage/types/core/types/files/file';

import { ErrorMessages } from '@/consts/errors';
import { getCloudStorageHeaders, withCloudStorageErrorHandling } from './utils';

/** Hidden JSON file (in the tool folder) that carries the saved slugs. */
export const SAVED_TEMPLATES_FILE = 'genai-saved-templates.json';

/**
 * Drive file attribute holding the saved slugs (a JSON-stringified string[]).
 * Slugs live in an attribute — not file content — because the drive's
 * content-update endpoint rejects `multipart/form-data` (415); attributes are
 * updated with a JSON PATCH, exactly like `userReaction` in `setFileReaction`.
 * Custom attributes must use the drive's `namespace~name` format.
 */
export const SAVED_TEMPLATES_ATTR = 'genai~savedTemplates';

export interface SavedTemplates {
  slugs: string[];
  /** Drive file id, or null when the file hasn't been created yet. */
  fileId: string | null;
}

const parseSlugs = (raw: unknown): string[] => {
  if (typeof raw !== 'string') return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((s): s is string => typeof s === 'string')
      : [];
  } catch {
    return [];
  }
};

/**
 * Reads the saved-templates slugs from the hidden tool file's attributes.
 * Returns the slugs and the file id (null if it doesn't exist yet), or null on error.
 */
export const getSavedTemplates = async ({
  toolId,
}: {
  toolId: string;
}): Promise<SavedTemplates | null> => {
  const result = await withCloudStorageErrorHandling({
    operation: () =>
      getFlattenedFiles({
        headers: getCloudStorageHeaders(),
        params: { limit: '100', offset: '0' },
        onlyHiddenFiles: true,
        attributes: { tool: toolId },
      }),
    errorMessage: ErrorMessages.loadDriveFiles,
  });

  if (!result) return null;

  const file = ((result.response ?? []) as IFile[]).find(
    (item) => item?.name === SAVED_TEMPLATES_FILE
  );

  if (!file?.uid) return { slugs: [], fileId: null };

  // Custom attributes round-trip in the listing at runtime (like
  // `userReaction`), even though they're absent from the closed IFile type.
  const attributes = file.attributes as Record<string, unknown> | undefined;

  return {
    slugs: parseSlugs(attributes?.[SAVED_TEMPLATES_ATTR]),
    fileId: file.uid,
  };
};

/** JSON-PATCH the saved slugs onto the file's attributes via the drive lib. */
const patchSlugs = ({ fileId, slugs }: { fileId: string; slugs: string[] }) =>
  withCloudStorageErrorHandling({
    operation: () =>
      updateFileMeta({
        headers: getCloudStorageHeaders(),
        fileId,
        // The drive expects an attributes object keyed by `namespace~name`, not
        // the typed FileAttributeProps[] array — cast to satisfy the signature.
        attributes: {
          [SAVED_TEMPLATES_ATTR]: JSON.stringify(slugs),
        } as unknown as FileAttributeProps[],
      }),
    errorMessage: ErrorMessages.loadDriveFiles,
  });

/**
 * Persists the saved-templates slugs. Updates the existing hidden file's
 * attributes, or creates the file (then patches it) on first save. Returns the
 * (possibly new) file id, or null on error.
 */
export const saveTemplates = async ({
  toolId,
  slugs,
  fileId,
}: {
  toolId: string;
  slugs: string[];
  fileId: string | null;
}): Promise<{ fileId: string | null } | null> => {
  let targetId = fileId;

  if (!targetId) {
    // First save: create the hidden placeholder file.
    const created = await withCloudStorageErrorHandling({
      operation: () =>
        uploadFileToSystemFolder({
          headers: getCloudStorageHeaders(),
          file: new File(['[]'], SAVED_TEMPLATES_FILE, {
            type: 'application/json',
          }),
          toolId,
          isFileHidden: true,
        }),
      errorMessage: ErrorMessages.loadDriveFiles,
    });

    targetId = created?.response?.uid ?? null;
    if (!targetId) return null;
  }

  const patched = await patchSlugs({ fileId: targetId, slugs });
  if (!patched) return null;

  return { fileId: targetId };
};
